import React from "react";
import { Box, Typography, Avatar, useMediaQuery } from "@mui/material";
import BackButton from "./BackButton";
import IconLinks from "./IconLinks";

/**
 * HeaderSection - Reusable header for public pages
 * Props:
 * - mainTitle: string (required)
 * - subTitle: string (optional)
 * - logo: image import or url (required)
 * - avatarAlt: string (optional)
 * - sx: object (optional, for Box overrides)
 */
const HeaderSection = ({
  mainTitle,
  subTitle,
  logo,
  avatarAlt = "DJ R",
  showBackButton = true,
  backFallbackUrl,
  sx = {},
}) => {
  const isMobile = useMediaQuery("(max-width:900px)");

  return (
    <Box
      spacing={1}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        maxWidth: "55rem",
        p: "0.5rem",
        m: 0,
        ...sx,
      }}
    >
      {isMobile ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            mb: 0,
          }}
        >
          {/* Top row: Back button aligned left (unchanged behavior) */}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "flex-start",
              mb: 0,
            }}
          >
            <Box
              sx={{
                width: "2.5rem",
                height: "2.5rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                m: 0,
                mr: 1,
                p: 0,
              }}
            >
              {showBackButton ? (
                <BackButton fallbackUrl={backFallbackUrl} />
              ) : null}
            </Box>
          </Box>

          {/* Avatar above the titles */}
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 1,
            }}
          >
            <Avatar
              src={logo}
              sx={{ width: "9rem", height: "9rem", mb: 1 }}
              alt={avatarAlt}
            />
          </Box>

          {/* Titles stacked and centered */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              px: 1,
              py: 0,
              m: 0,
              width: "100%",
            }}
          >
            <Typography
              sx={{
                fontSize: "1.2rem",
                letterSpacing: "0.2rem",
                fontWeight: 700,
                lineHeight: 1.1,
                textAlign: "center",
              }}
              variant="h3"
            >
              {mainTitle}
            </Typography>
            {subTitle && (
              <Typography
                sx={{
                  fontSize: "1rem",
                  letterSpacing: "0.15rem",
                  fontWeight: 500,
                  lineHeight: 1.1,
                  textAlign: "center",
                }}
                variant="h3"
              >
                {subTitle}
              </Typography>
            )}
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {/* Always-visible back button in the top-left corner */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "flex-start",
              justifyContent: "center",
              width: "100%",
              mb: 0,
            }}
          >
            {showBackButton && (
              <Box
                sx={{
                  width: "15%",
                  minWidth: "2.5rem",
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "center",
                }}
              >
                <BackButton />
              </Box>
            )}
            {/* Existing title / subtitle stack */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                mt: 0,
                mb: 1,
                ml: showBackButton ? -10 : 0,
                fontSize: "calc(0.5rem + 1.2vmin)",
                gap: "1rem",
                width: "85%",
              }}
            >
              <Typography
                sx={{
                  fontSize: "calc(1.75rem + 1vmin)",
                  letterSpacing: "0.5rem",
                }}
                variant="h3"
              >
                {mainTitle}
              </Typography>
              {subTitle && (
                <Typography
                  sx={{
                    fontSize: "calc(1.5rem + 1vmin)",
                    letterSpacing: "0.5rem",
                  }}
                  variant="h3"
                >
                  {subTitle}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Avatar below the titles */}
          <Avatar
            src={logo}
            sx={{ width: "15rem", height: "15rem", mb: "1rem" }}
            alt={avatarAlt}
          />
        </Box>
      )}
      <IconLinks />
    </Box>
  );
};

export default HeaderSection;
